import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'src/components/Button';
import Toast from 'src/components/Toast';
import { IPaginationParams, IPaginationResponse } from 'src/models/interfaces/pagination';
import { enStatusCode, IReservation } from 'src/models/interfaces/reservation';
import vaccinationService from 'src/service/vaccinationService';
import { VaccinationInfo } from 'src/types/IVaccination';
import GridItem from './gridItem';
import TablePagination from './pagination';
import { GridContainer, Header, MasterCheckbox, MessageTD, Table, TBody, TFoot, THead, CheckboxContainer } from './styles';

interface IProps {
  data: IPaginationResponse<VaccinationInfo>;
  error: boolean;
  loading: boolean;
  params: IPaginationParams;
  checkbox?: boolean;
  onCancel: (ids: number[]) => void;
  onAccept: (ids: number[]) => void;
  mergeParams: (newParams: Partial<IPaginationParams>) => void;
}

const DataGrid = (props: IProps) => {
  const { data, error, loading, params, checkbox = true, onCancel, mergeParams, onAccept } = props;
  const [checkedItems, setCheckedItems] = useState([] as any[]);
  const [allChecked, setAllChecked] = useState(false);
  const toast = Toast();

  const { total, results } = data || ({ total: 0, results: [] } as typeof data);

  const notCanceled = useMemo((): IReservation[] => {
    return (data?.results as any)?.filter((r: any) => r.statusCode !== enStatusCode.canceled);
  }, [data]);

  useEffect(() => {
    if (loading) {
      setAllChecked(false);
      setCheckedItems([]);
    }
  }, [loading]);

  const handleCheckAll = useCallback(
    ({ currentTarget: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
      setAllChecked(checked);
      setCheckedItems(checked ? notCanceled || [] : []);
    },
    [notCanceled]
  );

  const handleCheck = useCallback(
    (checked: boolean, item: any) => {
      const items = [...checkedItems];

      if (checked) items.push(item);
      else {
        let index = items.findIndex(i => i.id === item.id);
        items.splice(index, index > -1 ? 1 : 0);
      }

      setCheckedItems(items);
      setAllChecked(notCanceled.length === items.length);
    },
    [checkedItems, notCanceled]
  );

  const IsChecked = useCallback(
    (id: number) => {
      return checkedItems.find(i => i.id === id);
    },
    [checkedItems]
  );

  const handleCancelClick = useCallback(() => {
    const ids = checkedItems.map(i => i.id);
    onCancel(ids);
  }, [checkedItems, onCancel]);

  const handleAcceptClick = useCallback(() => {
    const ids = checkedItems.map(i => i.id);
    onAccept(ids);
  }, [checkedItems, onAccept]);

  const handleExport = useCallback(() => {
    vaccinationService
      .exportReport(params)
      .then(({ data }) => {
        const url = window.URL.createObjectURL(data);
        var a = document.createElement('a');
        a.href = url;
        a.download = 'Relatório de Vacinações.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error());
  }, [params]);

  return (
    <>
      <Header>

        {checkbox ?
          <CheckboxContainer>
            <MasterCheckbox id="select-all-checkboxes" checked={allChecked} onChange={handleCheckAll} />
            <label htmlFor="select-all-checkboxes">
              Selecionar todos
            </label>
          </CheckboxContainer> : ''}

        <Button fill={false} onClick={handleAcceptClick} disabled={checkedItems.length === 0}>
          Aceitar Selecionados
        </Button>
        <Button fill={false} onClick={handleCancelClick} disabled={checkedItems.length === 0}>
          Cancelar Selecionados
        </Button>
        <div className='right'>
          <Button fill={false} onClick={handleExport}>
            Exportar dados
          </Button>
        </div>
      </Header>
      <GridContainer>
        <Table>
          <THead>
            <tr>
              {checkbox ? <th></th> : ''}
              <th>Nome do Funcionário</th>
              <th>Interesse em se Vacinar</th>
              <th>Data do Agendamento</th>
              <th>Agendamento via Web App</th>
              <th>Status</th>
            </tr>
          </THead>
          <TBody>
            {error ? (
              <tr>
                <MessageTD>Houve um erro ao obter os registros.</MessageTD>
              </tr>
            ) : loading ? (
              <tr>
                <MessageTD>Carregando...</MessageTD>
              </tr>
            ) : results.length > 0 ? (
              results.map(d => (
                <GridItem key={d.id} checkbox={checkbox} isChecked={IsChecked} item={d} onClick={handleCheck} />
              ))
            ) : (
              <tr>
                <MessageTD>Nenhum registro encontrado.</MessageTD>
              </tr>
            )}
          </TBody>
          <TFoot>
            <tr>
              <TablePagination
                colSpan={30}
                total={total}
                disabled={loading}
                paginationParams={params}
                onChange={mergeParams}
              />
            </tr>
          </TFoot>
        </Table>
      </GridContainer>
    </>
  );
};

export default DataGrid;
